import axios from "axios";

import * as StorageUtils from "./StorageUtils";

import * as EnvironmentUtils from "./EnvironmentUtils";
import * as AnalyticsUtils from "./AnalyticsUtils";
import {
	EMULATED_FLEET_ID_KEY,
	EMULATED_GROUP_ID_KEY,
	EMULATED_USER_AUTH_LEVEL_KEY,
	EMULATED_USER_LEVEL_KEY,
	FLEETS_KEY,
	OVERRIDE_FLEET_KEY,
	OVERRIDE_PROGRAM_KEY,
	SELECTED_GROUP_KEY,
	SELECTED_ROOT_GROUP_KEY,
	USER_AUTH_LEVEL_KEY,
	USER_CLAIMS_KEY,
	USER_ID_KEY,
	USER_LEVEL_KEY,
	USER_OBJECT_KEY,
	USER_REFRESH_TOKEN_KEY,
	USER_TOKEN_KEY,
	TRENDS_AVAILABLE_KEY,
} from "../../StorageKeys";
import { User } from "../types/UserTypes";
import { getUserGroupId } from "./UserUtils";
import { postSignOut } from "../../Api";
import { AuthLevel } from "../consts/AuthConstants";

export interface Credential {
	token: string;
	refreshToken: string;
	expiresIn: number;
	user: User;
	uid?: string;
	userId?: string;
	userLevel: string;
	restricted: boolean;
	additionalFleets: string[];

	/** @deprecated */
	claims?: any;
}

export const getStoredUserObject = () => StorageUtils.get(USER_OBJECT_KEY);

export const isUserLoggedIn = () => StorageUtils.has(USER_ID_KEY);

const getAuthLevelValue = (name: string | undefined) => {
	return (AuthLevel as any)[name as any] || undefined;
};

export const isAuthLevelMet = (
	requiredLevel: number,
	disableEmulate?: boolean,
): boolean => {
	return (getCurrentUserLevel(disableEmulate) || 0) >= requiredLevel;
};

/** @deprecated */
export const old__getUserToken = (): string | undefined => {
	return StorageUtils.get(USER_TOKEN_KEY);
};

export const isSuperAdmin = (disableEmulate?: boolean): boolean => {
	return (getCurrentUserLevel(disableEmulate) || 0) >= AuthLevel.SUPER_ADMIN;
};

export const isAuthLevelAccessAnyFleet = () => {
	return isAuthLevelMet(AuthLevel.SALES_ADMIN);
};

export const isManagerOrBelow = (): boolean => {
	return (getCurrentUserLevel() || 0) <= AuthLevel.MANAGER;
};

export const isAdminOrAbove = (): boolean => {
	return (getCurrentUserLevel() || 0) >= AuthLevel.ADMIN;
};

export const isRestrictedMode = (): boolean => {
	const claims = StorageUtils.get(USER_CLAIMS_KEY);
	return Boolean(claims?.restricted);
};

export const getAuthLevelName = (userLevel?: number) => {
	if (!userLevel) return undefined;
	return Object.keys(AuthLevel).find(
		(key) => (AuthLevel as any)[key] === userLevel,
	);
};

export const setAuthToken = (auth: Credential) => {
	const { user, userId, claims } = auth;
	let userLevelName =
		auth.userLevel || getAuthLevelName(claims?.userLevel) || undefined;
	let userLevel;

	// user level is number
	if (userLevelName && !isNaN(userLevelName as any)) {
		userLevel = userLevelName;
		userLevelName = getAuthLevelName(userLevel as any);
	} else {
		userLevel = getAuthLevelValue(userLevelName);
	}

	StorageUtils.remove(USER_TOKEN_KEY);
	StorageUtils.remove(USER_LEVEL_KEY);
	StorageUtils.remove(USER_REFRESH_TOKEN_KEY);
	StorageUtils.set(USER_ID_KEY, userId ? userId : user.uid);

	if (userLevelName) {
		StorageUtils.set(USER_CLAIMS_KEY, {
			...auth,
			refreshToken: undefined,
			token: undefined,
		} as any);
		StorageUtils.set(USER_AUTH_LEVEL_KEY, userLevel);
	}

	if (
		!StorageUtils.get(EMULATED_USER_AUTH_LEVEL_KEY) &&
		StorageUtils.get(EMULATED_USER_LEVEL_KEY)
	) {
		StorageUtils.set(
			EMULATED_USER_AUTH_LEVEL_KEY,
			getAuthLevelValue(StorageUtils.get(EMULATED_USER_LEVEL_KEY)),
		);
	}
};

export const removeAuthToken = async () => {
	StorageUtils.remove(USER_TOKEN_KEY);
	StorageUtils.remove(USER_REFRESH_TOKEN_KEY);
	StorageUtils.remove(USER_ID_KEY);
	StorageUtils.remove(USER_LEVEL_KEY);
	StorageUtils.remove(USER_AUTH_LEVEL_KEY);
	StorageUtils.remove(USER_OBJECT_KEY);
	StorageUtils.remove(USER_CLAIMS_KEY);

	StorageUtils.remove(FLEETS_KEY);
	StorageUtils.remove(OVERRIDE_FLEET_KEY);
	StorageUtils.remove(OVERRIDE_PROGRAM_KEY);
	StorageUtils.remove(SELECTED_GROUP_KEY);
	StorageUtils.remove(SELECTED_ROOT_GROUP_KEY);
	StorageUtils.remove(TRENDS_AVAILABLE_KEY);
	AnalyticsUtils.userChange(null);

	await postSignOut.submit({});
};

export const getCurrentUserLevel = (
	disableEmulate?: boolean,
): number | undefined => {
	const userLevel =
		(!disableEmulate && StorageUtils.get(EMULATED_USER_AUTH_LEVEL_KEY)) ||
		StorageUtils.get(USER_AUTH_LEVEL_KEY);
	if (userLevel) {
		return userLevel;
	}

	// TODO: legacy -- remove
	const userLevelName =
		(!disableEmulate && StorageUtils.get(EMULATED_USER_LEVEL_KEY)) ||
		StorageUtils.get(USER_LEVEL_KEY);
	return getAuthLevelValue(userLevelName);
};

let refreshPromise: Promise<string> | null = null;

export const possiblyWaitForTokenRefresh = async () => {
	return refreshPromise;
};

export const getTokenMetadata = () => {
	const value = `; ${document.cookie}`;
	const parts = value.split("; brightmile_token_metadata=");
	if (parts.length === 2) {
		const string = parts.pop()!.split(";").shift()!;
		return JSON.parse(decodeURIComponent(string));
	}
	return undefined;
};

export const refreshAuthToken = (): Promise<string> => {
	if (refreshPromise) return refreshPromise;
	return (refreshPromise = new Promise(async (resolve, reject) => {
		try {
			const refreshToken = StorageUtils.get(USER_REFRESH_TOKEN_KEY);

			console.warn("Attempting to refresh user token");

			// this MUST not use our API_DEF stuff as the middleware will wait upon the refreshPromise and get stuck forever
			const res = await axios({
				method: "post",
				url: `${EnvironmentUtils.getBaseUrl()}/auth/refresh-token`,
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
					ApiKey: EnvironmentUtils.getApiKey(),
					Authorization: refreshToken,
				},
				withCredentials: true,
			});
			refreshPromise = null;
			const { token } = res.data;
			setAuthToken(res.data);
			resolve(token);
		} catch (error) {
			refreshPromise = null;
			console.error("Could not refresh token", error);
			reject(error);
		}
	}));
};

export const canAccessRoot = (user?: User | null) => {
	// maybe make store user?
	const resolvedUser = user || getStoredUserObject();
	return (
		resolvedUser &&
		((!getUserGroupId(resolvedUser) && isManagerOrBelow()) || isAdminOrAbove())
	);
};

export const getUserClaims = () => {
	return StorageUtils.get(USER_CLAIMS_KEY);
};

export const injectToken = (data: any) => {
	for (const key in data) {
		const value = data[key];
		localStorage.setItem(key, JSON.stringify(value, null, 2));
	}
	window.location.reload();
};

export const getEmulatedFleetId = () => {
	return StorageUtils.get(EMULATED_FLEET_ID_KEY);
};

export const setEmulatedFleetId = (fleetId: string | undefined) => {
	return StorageUtils.set(EMULATED_FLEET_ID_KEY, fleetId);
};

export const getEmulatedGroupId = () => {
	return StorageUtils.get(EMULATED_GROUP_ID_KEY);
};

export const setEmulatedGroupId = (groupId: string | undefined | null) => {
	return StorageUtils.set(EMULATED_GROUP_ID_KEY, groupId);
};

export const getEmulatedUserLevel = (): number | undefined => {
	return StorageUtils.get(EMULATED_USER_AUTH_LEVEL_KEY);
};

export const setEmulatedUserLevel = (userLevel: number | undefined) => {
	return StorageUtils.set(EMULATED_USER_AUTH_LEVEL_KEY, userLevel);
};

export const resetEmulatedUserLevel = () => {
	setEmulatedUserLevel(undefined);
	setEmulatedFleetId(undefined);
	setEmulatedGroupId(undefined);
};

export const isMultiFactorMandatedButNotEnabled = () => {
	const loggedInUser = getStoredUserObject();
	return (
		loggedInUser?.multiFactor?.mandated && !loggedInUser.multiFactor.enabled
	);
};

export const isSignUpFinished = () => {
	const loggedInUser = getStoredUserObject();
	return loggedInUser?.finishedSignUp === false;
};
